import React, { useEffect, useRef, useState } from 'react';
import {
	Modal,
	Progress,
	Typography,
	Alert,
	Spin,
	Space,
	Card,
	List,
	Divider,
	Button
} from 'antd';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faWandMagicSparkles,
	faMagnifyingGlassChart,
	faRobot,
	faUsers,
	faChartLine,
	faExclamationTriangle,
	faCheckCircle,
	faGears,
	faFileLines,
	faChevronRight,
	faCheck,
	faLightbulb,
	faPlus,
	faSpinnerThird
} from '@fortawesome/pro-light-svg-icons';
import {
	useScenarioMessages,
	useApplyScenarioChanges,
	ApplyChangesContext
} from '../../services/scenarioMessages';
import { ProgressStep } from '../../services/scenarioMessages/types';
import './ScenarioElaborationModal.sass';

const { Text, Title, Paragraph } = Typography;

/**
 * Helper pour extraire le texte d'analyse propre
 * Gère le cas où l'analyse contient du JSON brut ou du double-encodage
 */
const extractAnalysisText = (analysis: string | null): string | null => {
	if (!analysis) return null;

	let text = analysis;

	// Détecter et corriger le double-encodage JSON (\\n, \\", etc.)
	if (text.includes('\\n') || text.includes('\\"') || text.includes('\\\\')) {
		try {
			// Tenter de décoder le JSON doublement encodé
			text = JSON.parse(`"${text.replace(/"/g, '\\"')}"`);
		} catch {
			// Si ça échoue, essayer une autre approche
			text = text
				.replace(/\\n/g, '\n')
				.replace(/\\"/g, '"')
				.replace(/\\\\/g, '\\');
		}
	}

	const trimmed = text.trim();

	// Si ça commence par ```json ou { ou [, c'est probablement du JSON brut
	if (trimmed.startsWith('```json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
		try {
			// Nettoyer le markdown si présent
			let jsonStr = trimmed;
			if (jsonStr.startsWith('```json')) {
				jsonStr = jsonStr.replace(/^```json\s*/i, '').replace(/\s*```$/i, '');
			}

			const parsed = JSON.parse(jsonStr);

			// Si c'est un objet avec un champ 'analysis', l'utiliser
			if (parsed && typeof parsed.analysis === 'string') {
				return extractAnalysisText(parsed.analysis); // Récursion pour gérer les cas imbriqués
			}

			// Si c'est un objet avec scenarioDescription, utiliser ça
			if (parsed && typeof parsed.scenarioDescription === 'string') {
				return parsed.scenarioDescription;
			}

			// Sinon, ne pas afficher le JSON brut
			return null;
		} catch {
			// Si le parsing échoue mais que ça ressemble quand même à du JSON, ne pas afficher
			if (trimmed.includes('"scenarioName"') || trimmed.includes('"modifications"')) {
				return null;
			}
		}
	}

	// Si le texte contient clairement des structures JSON, ne pas l'afficher
	if (trimmed.includes('"stakeholders":') || trimmed.includes('"newElements":')) {
		return null;
	}

	// C'est du texte normal, le retourner
	return text;
};

interface ScenarioElaborationModalProps {
	visible: boolean;
	projectId: string;
	scenarioName: string;
	scenarioId: string;
	query: string;
	// Contexte du projet pour appliquer les changements
	scenario: any;
	stakeholders: any[];
	stakes: any[];
	risks: any[];
	indicators: any[];
	scenarios: any[];
	// Échelles pour normaliser les valeurs
	likelihoods?: any[];
	impacts?: any[];
	onComplete: () => void;
	onClose: () => void;
}

// Configuration des étapes avec icônes
const STEP_CONFIG: Record<
	ProgressStep,
	{ icon: any; labelKey: string }
> = {
	started: {
		icon: faWandMagicSparkles,
		labelKey: 'messages.scenario_elaboration.steps.started'
	},
	fetching_project: {
		icon: faFileLines,
		labelKey: 'messages.scenario_elaboration.steps.fetching_project'
	},
	deep_research: {
		icon: faMagnifyingGlassChart,
		labelKey: 'messages.scenario_elaboration.steps.deep_research'
	},
	ai_analysis: {
		icon: faRobot,
		labelKey: 'messages.scenario_elaboration.steps.ai_analysis'
	},
	reformatting: {
		icon: faGears,
		labelKey: 'messages.scenario_elaboration.steps.reformatting'
	},
	processing_stakeholders: {
		icon: faUsers,
		labelKey: 'messages.scenario_elaboration.steps.processing_stakeholders'
	},
	processing_stakes: {
		icon: faChartLine,
		labelKey: 'messages.scenario_elaboration.steps.processing_stakes'
	},
	processing_risks: {
		icon: faExclamationTriangle,
		labelKey: 'messages.scenario_elaboration.steps.processing_risks'
	},
	processing_constraints: {
		icon: faGears,
		labelKey: 'messages.scenario_elaboration.steps.processing_constraints'
	},
	scenarios_started: {
		icon: faLightbulb,
		labelKey: 'messages.scenario_elaboration.steps.scenarios'
	},
	scenario_progress: {
		icon: faLightbulb,
		labelKey: 'messages.scenario_elaboration.steps.scenarios'
	},
	completed: {
		icon: faCheckCircle,
		labelKey: 'messages.scenario_elaboration.steps.completed'
	},
	error: {
		icon: faExclamationTriangle,
		labelKey: 'messages.scenario_elaboration.steps.error'
	}
};

// Ordre des étapes pour le composant Steps
const STEP_ORDER: ProgressStep[] = [
	'started',
	'fetching_project',
	'deep_research',
	'ai_analysis',
	'reformatting',
	'processing_stakeholders',
	'processing_stakes',
	'processing_risks',
	'processing_constraints',
	'scenarios_started',
	'completed'
];

export const ScenarioElaborationModal: React.FC<
	ScenarioElaborationModalProps
> = ({
	visible,
	projectId,
	scenarioName,
	scenarioId,
	query,
	scenario,
	stakeholders,
	stakes,
	risks,
	indicators,
	scenarios,
	likelihoods,
	impacts,
	onComplete,
	onClose
}) => {
		const { t } = useTranslation();
		const prevVisibleRef = useRef(visible);
		const [appliedChanges, setAppliedChanges] = useState<{
			stakeholders: number;
			stakes: number;
			risks: number;
			indicators: number;
		}>({ stakeholders: 0, stakes: 0, risks: 0, indicators: 0 });

		const [createdElements, setCreatedElements] = useState<{
			stakeholders: number;
			stakes: number;
			risks: number;
			indicators: number;
		}>({ stakeholders: 0, stakes: 0, risks: 0, indicators: 0 });

		// Hook pour appliquer les changements
		const {
			applyStakeholderChanges,
			applyIndicatorChanges,
			applyRiskChanges,
			applyConstraintChanges,
			createNewStakeholders,
			createNewStakes,
			createNewRisks,
			createNewIndicators,
			resetPlacedPositions
		} = useApplyScenarioChanges();

		// Contexte pour appliquer les changements
		const applyContext: ApplyChangesContext = {
			projectId,
			scenario,
			stakeholders,
			stakes: stakes,
			risks,
			indicators,
			scenarios,
			likelihoods,
			impacts
		};

		const {
			isRunning,
			progress,
			currentStep,
			error,
			scenarioInfo,
			results,
			isComplete,
			reset,
			startTracking
		} = useScenarioMessages({
			projectId,
			enabled: visible,
			callbacks: {
				// Appliquer les modifications et créer les nouveaux éléments dès réception
				onObjectResult: async (objectType, modifications, newElements) => {

					if (objectType === 'stakeholders') {
						if (modifications.length > 0) {
							const result = await applyStakeholderChanges(
								modifications as any,
								applyContext
							);
							setAppliedChanges((prev) => ({
								...prev,
								stakeholders: result.applied
							}));
						}
						if (newElements.length > 0) {
							const createResult = await createNewStakeholders(
								newElements as any,
								applyContext
							);
							setCreatedElements((prev) => ({
								...prev,
								stakeholders: createResult.applied
							}));
						}
					} else if (objectType === 'stakes') {
						if (modifications.length > 0) {
							const result = await applyIndicatorChanges(
								modifications as any,
								applyContext
							);
							setAppliedChanges((prev) => ({
								...prev,
								stakes: result.applied
							}));
						}
						if (newElements.length > 0) {
							const createResult = await createNewStakes(
								newElements as any,
								applyContext
							);
							setCreatedElements((prev) => ({
								...prev,
								stakes: createResult.applied
							}));
						}
					} else if (objectType === 'risks') {
						if (modifications.length > 0) {
							const result = await applyRiskChanges(
								modifications as any,
								applyContext
							);
							setAppliedChanges((prev) => ({
								...prev,
								risks: result.applied
							}));
						}
						if (newElements.length > 0) {
							const createResult = await createNewRisks(
								newElements as any,
								applyContext
							);
							setCreatedElements((prev) => ({
								...prev,
								risks: createResult.applied
							}));
						}
					} else if (objectType === 'indicators') {
						if (modifications.length > 0) {
							const result = await applyConstraintChanges(
								modifications as any,
								applyContext
							);
							setAppliedChanges((prev) => ({
								...prev,
								indicators: result.applied
							}));
						}
						if (newElements.length > 0) {
							const createResult = await createNewIndicators(
								newElements as any,
								applyContext
							);
							setCreatedElements((prev) => ({
								...prev,
								indicators: createResult.applied
							}));
						}
					}
				},
				onComplete: () => {
					setTimeout(() => {
						onComplete();
					}, 1500);
				},
				onError: (err) => {
					console.error('Scenario elaboration error:', err);
				}
			}
		});

		// Reset et démarrer le tracking quand le modal s'ouvre
		useEffect(() => {
			if (visible && !prevVisibleRef.current) {
				// Reset tous les états au démarrage
				reset();
				resetPlacedPositions(); // Reset les positions calculées
				setAppliedChanges({
					stakeholders: 0,
					stakes: 0,
					risks: 0,
					indicators: 0
				});
				setCreatedElements({
					stakeholders: 0,
					stakes: 0,
					risks: 0,
					indicators: 0
				});
				if (query) {
					startTracking(query);
				}
			} else if (!visible && prevVisibleRef.current) {
				// Nettoyage quand le modal se ferme
				reset();
				resetPlacedPositions();
			}
			prevVisibleRef.current = visible;
		}, [visible, query, reset, startTracking, resetPlacedPositions]);

		// Nettoyage au démontage du composant
		useEffect(() => {
			return () => {
				reset();
				resetPlacedPositions();
			};
		}, [reset, resetPlacedPositions]);

		// Calculer l'index de l'étape actuelle
		const currentStepIndex = currentStep
			? STEP_ORDER.indexOf(currentStep)
			: -1;

		// Helper pour afficher les modifications (simplifié sans tags)
		const renderModificationItem = (
			mod: any,
			entityList: any[],
			entityType: string
		) => {
			// Priorité : mod.name > entity.name dans la liste > null
			const entity = entityList.find((e) => e.id === mod.id);
			const entityName = mod.name || entity?.name;

			// Si on a un nom, afficher nom + reason
			// Sinon, utiliser la reason comme contenu principal
			if (entityName) {
				return (
					<List.Item key={mod.id} className="modification-item">
						<div className="modification-content">
							<Text strong className="entity-name">{entityName}</Text>
							{mod.reason && (
								<Text type="secondary" className="modification-reason">
									{mod.reason}
								</Text>
							)}
						</div>
					</List.Item>
				);
			}

			// Pas de nom trouvé - utiliser reason comme contenu principal
			return (
				<List.Item key={mod.id} className="modification-item">
					<div className="modification-content">
						<Text className="entity-name">
							{mod.reason || `Modification ${mod.id.substring(0, 8)}...`}
						</Text>
					</div>
				</List.Item>
			);
		};

		const totalApplied =
			appliedChanges.stakeholders +
			appliedChanges.stakes +
			appliedChanges.risks +
			appliedChanges.indicators;

		const totalCreated =
			createdElements.stakeholders +
			createdElements.stakes +
			createdElements.risks +
			createdElements.indicators;

		// Helper pour afficher les nouveaux éléments
		const renderNewElementItem = (element: any) => {
			return (
				<List.Item key={element.name} className="modification-item new-element">
					<div className="modification-content">
						<Text strong className="entity-name">{element.name}</Text>
						{element.reason && (
							<Text type="secondary" className="modification-reason">
								{element.reason}
							</Text>
						)}
					</div>
				</List.Item>
			);
		};

		// Rendu de la barre d'étapes avec chevrons
		const renderStepBar = () => {
			const stepsToShow = STEP_ORDER.slice(0, -1); // Exclure 'completed'

			return (
				<div className="step-bar">
					{stepsToShow.map((step, index) => {
						const config = STEP_CONFIG[step];
						const isCompleted = index < currentStepIndex;
						const isCurrent = index === currentStepIndex;
						const isError = error && isCurrent;

						let statusClass = 'waiting';
						if (isCompleted) statusClass = 'completed';
						else if (isCurrent) statusClass = isError ? 'error' : 'active';

						return (
							<React.Fragment key={step}>
								<div className={`step-item ${statusClass}`}>
									<div className="step-icon">
										{isCompleted ? (
											<FontAwesomeIcon icon={faCheck} />
										) : isCurrent && !isError ? (
											<FontAwesomeIcon icon={faSpinnerThird} spin />
										) : (
											<FontAwesomeIcon icon={config.icon} />
										)}
									</div>
									<span className="step-label">{t(config.labelKey)}</span>
								</div>
								{index < stepsToShow.length - 1 && (
									<div className={`step-chevron ${isCompleted ? 'completed' : ''}`}>
										<FontAwesomeIcon icon={faChevronRight} />
									</div>
								)}
							</React.Fragment>
						);
					})}
				</div>
			);
		};

		return (
			<Modal
				open={visible}
				title={
					<Space>
						<FontAwesomeIcon icon={faWandMagicSparkles} />
						{t('messages.scenario_elaboration.title')}
					</Space>
				}
				closable={isComplete || !!error}
				maskClosable={false}
				keyboard={false}
				onCancel={isComplete || error ? onClose : undefined}
				footer={
					isComplete ? (
						<Button type="primary" onClick={onClose}>
							{t('actions.close')}
						</Button>
					) : null
				}
				width={900}
				className="ScenarioElaborationModal"
			>
				{/* En-tête avec nom et hypothèse */}
				<Card size="small" className="elaboration-header-card">
					<Title level={5} style={{ margin: 0 }}>
						{scenarioName}
					</Title>
					<Text type="secondary">{query}</Text>
				</Card>

				{/* Barre de progression */}
				<div className="progress-section">
					<Progress
						percent={progress}
						status={
							error ? 'exception' : isComplete ? 'success' : 'active'
						}
						strokeColor={{
							'0%': '#1677ff',
							'100%': '#52c41a'
						}}
						format={(percent) => `${percent}%`}
					/>
				</div>

				{/* Barre d'étapes avec chevrons */}
				{renderStepBar()}

				{error && (
					<Alert
						type="error"
						showIcon
						message={t('messages.scenario_elaboration.error_title')}
						description={error}
						className="error-alert"
					/>
				)}

				{/* Analyse préliminaire */}
				{(() => {
					const analysisText = extractAnalysisText(scenarioInfo.analysis);
					return analysisText ? (
						<Card
							size="small"
							title={t('messages.scenario_elaboration.analysis_preview')}
							className="analysis-card"
						>
							<Paragraph>{analysisText}</Paragraph>
						</Card>
					) : null;
				})()}

				{/* Résultats des modifications */}
				{(results.stakeholders ||
					results.stakes ||
					results.risks ||
					results.indicators) && (
						<div className="results-section">
							<Divider>
								{t('messages.scenario_elaboration.applied_changes')} (
								{totalApplied})
								{totalCreated > 0 && (
									<span className="created-count">
										{' '}+ {totalCreated} {t('messages.scenario_elaboration.new_elements')}
									</span>
								)}
							</Divider>

							{/* Stakeholders */}
							{results.stakeholders &&
								(results.stakeholders.modifications.length > 0 ||
									results.stakeholders.newElements.length > 0) && (
									<Card
										size="small"
										title={
											<span className="result-card-title">
												<FontAwesomeIcon icon={faUsers} />
												<span>
													{t('pages.navigator.title')} ({appliedChanges.stakeholders})
													{createdElements.stakeholders > 0 && (
														<span className="new-badge">
															<FontAwesomeIcon icon={faPlus} /> {createdElements.stakeholders}
														</span>
													)}
												</span>
												<FontAwesomeIcon icon={faCheck} className="check-icon" />
											</span>
										}
										className="result-card"
									>
										{results.stakeholders.modifications.length > 0 && (
											<List
												size="small"
												dataSource={
													results.stakeholders.modifications
												}
												renderItem={(mod) =>
													renderModificationItem(
														mod,
														stakeholders,
														'stakeholder'
													)
												}
											/>
										)}
										{results.stakeholders.newElements.length > 0 && (
											<>
												{results.stakeholders.modifications.length > 0 && (
													<Divider plain style={{ margin: '8px 0', fontSize: '12px' }}>
														{t('messages.scenario_elaboration.new_elements')}
													</Divider>
												)}
												<List
													size="small"
													dataSource={results.stakeholders.newElements}
													renderItem={renderNewElementItem}
													className="new-elements-list"
												/>
											</>
										)}
									</Card>
								)}

							{/* Indicators */}
							{results.stakes &&
								(results.stakes.modifications.length > 0 ||
									results.stakes.newElements.length > 0) && (
									<Card
										size="small"
										title={
											<span className="result-card-title">
												<FontAwesomeIcon icon={faChartLine} />
												<span>
													{t('pages.stakes.title')} ({appliedChanges.stakes})
													{createdElements.stakes > 0 && (
														<span className="new-badge">
															<FontAwesomeIcon icon={faPlus} /> {createdElements.stakes}
														</span>
													)}
												</span>
												<FontAwesomeIcon icon={faCheck} className="check-icon" />
											</span>
										}
										className="result-card"
									>
										{results.stakes.modifications.length > 0 && (
											<List
												size="small"
												dataSource={
													results.stakes.modifications
												}
												renderItem={(mod) =>
													renderModificationItem(
														mod,
														stakes,
														'stake'
													)
												}
											/>
										)}
										{results.stakes.newElements.length > 0 && (
											<>
												{results.stakes.modifications.length > 0 && (
													<Divider plain style={{ margin: '8px 0', fontSize: '12px' }}>
														{t('messages.scenario_elaboration.new_elements')}
													</Divider>
												)}
												<List
													size="small"
													dataSource={results.stakes.newElements}
													renderItem={renderNewElementItem}
													className="new-elements-list"
												/>
											</>
										)}
									</Card>
								)}

							{/* Risks (non-opportunities) */}
							{results.risks && (results.risks.modifications.length > 0 || results.risks.newElements.length > 0) && (() => {
								// Séparer risques et opportunités (modifications)
								const riskMods = results.risks.modifications.filter((mod: any) => {
									const entity = risks.find((r) => r.id === mod.id);
									return entity && !entity.isOpportunity;
								});
								const opportunityMods = results.risks.modifications.filter((mod: any) => {
									const entity = risks.find((r) => r.id === mod.id);
									return entity && entity.isOpportunity;
								});

								// Séparer risques et opportunités (nouveaux éléments)
								const newRisks = results.risks.newElements.filter((el: any) => !el.isOpportunity);
								const newOpportunities = results.risks.newElements.filter((el: any) => el.isOpportunity);

								// Compter les risques et opportunités créés
								const createdRisksCount = newRisks.length;
								const createdOpportunitiesCount = newOpportunities.length;

								return (
									<>
										{(riskMods.length > 0 || newRisks.length > 0) && (
											<Card
												size="small"
												title={
													<span className="result-card-title">
														<FontAwesomeIcon icon={faExclamationTriangle} />
														<span>
															{t('pages.risks.risks')} ({riskMods.length})
															{createdRisksCount > 0 && (
																<span className="new-badge">
																	<FontAwesomeIcon icon={faPlus} /> {createdRisksCount}
																</span>
															)}
														</span>
														<FontAwesomeIcon icon={faCheck} className="check-icon" />
													</span>
												}
												className="result-card"
											>
												{riskMods.length > 0 && (
													<List
														size="small"
														dataSource={riskMods}
														renderItem={(mod) =>
															renderModificationItem(mod, risks, 'risk')
														}
													/>
												)}
												{newRisks.length > 0 && (
													<>
														{riskMods.length > 0 && (
															<Divider plain style={{ margin: '8px 0', fontSize: '12px' }}>
																{t('messages.scenario_elaboration.new_elements')}
															</Divider>
														)}
														<List
															size="small"
															dataSource={newRisks}
															renderItem={renderNewElementItem}
															className="new-elements-list"
														/>
													</>
												)}
											</Card>
										)}

										{(opportunityMods.length > 0 || newOpportunities.length > 0) && (
											<Card
												size="small"
												title={
													<span className="result-card-title">
														<FontAwesomeIcon icon={faLightbulb} />
														<span>
															{t('pages.risks.opportunities')} ({opportunityMods.length})
															{createdOpportunitiesCount > 0 && (
																<span className="new-badge">
																	<FontAwesomeIcon icon={faPlus} /> {createdOpportunitiesCount}
																</span>
															)}
														</span>
														<FontAwesomeIcon icon={faCheck} className="check-icon" />
													</span>
												}
												className="result-card opportunity-card"
											>
												{opportunityMods.length > 0 && (
													<List
														size="small"
														dataSource={opportunityMods}
														renderItem={(mod) =>
															renderModificationItem(mod, risks, 'opportunity')
														}
													/>
												)}
												{newOpportunities.length > 0 && (
													<>
														{opportunityMods.length > 0 && (
															<Divider plain style={{ margin: '8px 0', fontSize: '12px' }}>
																{t('messages.scenario_elaboration.new_elements')}
															</Divider>
														)}
														<List
															size="small"
															dataSource={newOpportunities}
															renderItem={renderNewElementItem}
															className="new-elements-list"
														/>
													</>
												)}
											</Card>
										)}
									</>
								);
							})()}

							{/* Constraints */}
							{results.indicators &&
								(results.indicators.modifications.length > 0 ||
									results.indicators.newElements.length > 0) && (
									<Card
										size="small"
										title={
											<span className="result-card-title">
												<FontAwesomeIcon icon={faGears} />
												<span>
													{t('pages.indicators.title')} ({appliedChanges.indicators})
													{createdElements.indicators > 0 && (
														<span className="new-badge">
															<FontAwesomeIcon icon={faPlus} /> {createdElements.indicators}
														</span>
													)}
												</span>
												<FontAwesomeIcon icon={faCheck} className="check-icon" />
											</span>
										}
										className="result-card"
									>
										{results.indicators.modifications.length > 0 && (
											<List
												size="small"
												dataSource={
													results.indicators.modifications
												}
												renderItem={(mod) =>
													renderModificationItem(
														mod,
														indicators,
														'indicator'
													)
												}
											/>
										)}
										{results.indicators.newElements.length > 0 && (
											<>
												{results.indicators.modifications.length > 0 && (
													<Divider plain style={{ margin: '8px 0', fontSize: '12px' }}>
														{t('messages.scenario_elaboration.new_elements')}
													</Divider>
												)}
												<List
													size="small"
													dataSource={results.indicators.newElements}
													renderItem={renderNewElementItem}
													className="new-elements-list"
												/>
											</>
										)}
									</Card>
								)}
						</div>
					)}

				{isComplete && (
					<Alert
						type="success"
						showIcon
						message={t('messages.scenario_elaboration.complete_title')}
						description={t(
							'messages.scenario_elaboration.complete_description'
						)}
						className="success-alert"
					/>
				)}

				{isRunning && !currentStep && (
					<div className="initial-loading">
						<Spin size="large" />
						<Text type="secondary">
							{t('messages.scenario_elaboration.initializing')}
						</Text>
					</div>
				)}
			</Modal>
		);
	};

export default ScenarioElaborationModal;
